import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { Loader2, Printer, ArrowLeft } from "lucide-react";
import { Attachments } from "@/components/Attachments";

const MODE_LABEL = { cash: "Cash", bank_transfer: "Bank transfer", cheque: "Cheque", card: "Card", online: "Online", unspecified: "—" };

export default function Invoice() {
  const { oid, token } = useParams();
  const isPublic = !!token;
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const url = isPublic ? `/public/invoice/${token}` : `/admin/orders/${oid}/invoice`;
    api.get(url).then((r) => setInv(r.data)).catch((e) => setErr(e.response?.data?.detail || "Not found"));
  }, [oid, token, isPublic]);

  if (err) return <div className="p-10 text-center text-muted-foreground">{String(err)}</div>;
  if (!inv) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const co = inv.company || {};
  const cu = inv.customer || {};
  const isCN = inv.is_return;
  const statusVariant = isCN ? "secondary" : inv.payment_status === "paid" ? "default" : inv.payment_status === "partial" ? "secondary" : "destructive";
  const vatPct = inv.lines[0] ? Math.round(inv.lines[0].vat_rate * 100) : 5;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto p-6 sm:p-10 print:p-0">
        <div className="flex items-center justify-between mb-6 print:hidden">
          {isPublic ? <span className="text-xs text-muted-foreground" data-testid="invoice-public-label">Shared invoice · read-only</span>
            : <Button variant="ghost" onClick={() => navigate(-1)} data-testid="invoice-back-button"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>}
          <Button onClick={() => window.print()} data-testid="invoice-print-button"><Printer className="w-4 h-4 mr-1" /> Print / Save PDF</Button>
        </div>

        <div className="rounded-xl border border-border bg-card p-8 print:border-0 print:shadow-none" data-testid="invoice-sheet">
          <div className="flex items-start justify-between gap-6">
            <div className="text-sm">
              <Logo size={30} />
              <p className="font-semibold mt-3" data-testid="invoice-company-name">{co.legal_name || co.name}</p>
              {co.trn && <p className="text-xs text-muted-foreground" data-testid="invoice-company-trn">TRN {co.trn}</p>}
              <p className="text-xs text-muted-foreground">{[co.address, co.city, co.country].filter(Boolean).join(", ")}</p>
              <p className="text-xs text-muted-foreground">{[co.phone, co.email, co.website].filter(Boolean).join(" · ")}</p>
            </div>
            <div className="text-right">
              <p className="font-display text-2xl font-bold tracking-tight" data-testid="invoice-title">{isCN ? "TAX CREDIT NOTE" : "TAX INVOICE"}</p>
              <p className="font-mono text-sm mt-1" data-testid="invoice-number">{inv.invoice_no}</p>
              {isCN && inv.original && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="invoice-credit-note-for">
                  against {isPublic ? inv.original.invoice_no : <Link to={`/invoice/${inv.original.order_id}`} className="text-primary hover:underline">{inv.original.invoice_no}</Link>} · {inv.original.date}
                </p>
              )}
              <Badge variant={statusVariant} className="mt-2 capitalize" data-testid="invoice-status-badge">{isCN ? "credit applied" : inv.payment_status === "partial" ? "partially paid" : inv.payment_status}</Badge>
              {inv.approval_status === "pending" && <Badge variant="outline" className="mt-2 ml-1">awaiting approval</Badge>}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 text-sm">
            <div className="col-span-2">
              <p className="text-xs text-muted-foreground">Bill to</p>
              <p className="font-medium" data-testid="invoice-customer-name">{cu.name}</p>
              {cu.trn && <p className="text-xs text-muted-foreground" data-testid="invoice-customer-trn">TRN {cu.trn}</p>}
              {cu.address && <p className="text-xs text-muted-foreground">{cu.address}</p>}
              <p className="text-xs text-muted-foreground">{[cu.contact_person, cu.phone, cu.email].filter(Boolean).join(" · ")}</p>
            </div>
            <div><p className="text-xs text-muted-foreground">{isCN ? "Credit note date" : "Invoice date"}</p><p className="font-medium">{inv.date}</p><p className="text-xs text-muted-foreground mt-1">Agent {inv.agent.name}{inv.agent.agent_code ? ` · ${inv.agent.agent_code}` : ""}</p></div>
            {!isCN && <div><p className="text-xs text-muted-foreground">Due date ({inv.credit_days} days)</p><p className="font-medium">{inv.due_date}</p>{inv.paid_date && <p className="text-xs text-primary">Settled {inv.paid_date}</p>}</div>}
          </div>

          <table className="w-full text-sm mt-8">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-medium py-2">Description</th>
                <th className="text-right font-medium py-2">Qty</th>
                <th className="text-right font-medium py-2">Disc.</th>
                <th className="text-right font-medium py-2">Unit (ex-VAT)</th>
                <th className="text-right font-medium py-2">Amount (ex-VAT)</th>
                <th className="text-right font-medium py-2">VAT {vatPct}%</th>
                <th className="text-right font-medium py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {inv.lines.map((l, i) => (
                <tr key={i} className="border-b border-border/40">
                  <td className="py-2.5">{l.product}</td>
                  <td className="py-2.5 text-right">{l.qty}</td>
                  <td className="py-2.5 text-right">{l.discount_level}%</td>
                  <td className="py-2.5 text-right">{money(l.unit_ex_vat)}</td>
                  <td className="py-2.5 text-right">{money(l.line_ex_vat)}</td>
                  <td className="py-2.5 text-right text-muted-foreground">{money(l.vat_amount)}</td>
                  <td className="py-2.5 text-right font-medium">{money(l.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex flex-wrap justify-between gap-6 mt-6">
            <div className="text-xs text-muted-foreground max-w-xs space-y-1">
              {(co.bank_name || co.iban) && !isCN && (
                <div data-testid="invoice-bank-details">
                  <p className="font-medium text-foreground">Pay by bank transfer</p>
                  {co.bank_name && <p>{co.bank_name}{co.bank_account_name ? ` · ${co.bank_account_name}` : ""}</p>}
                  {co.iban && <p>IBAN {co.iban}</p>}
                  {co.swift && <p>SWIFT {co.swift}</p>}
                  <p>Reference: {inv.invoice_no}</p>
                </div>
              )}
              {inv.notes && <p>Note: {inv.notes}</p>}
            </div>
            <div className="w-72 space-y-1.5 text-sm">
              <div className="flex justify-between text-muted-foreground"><span>Subtotal (ex-VAT)</span><span>{money(inv.subtotal_ex_vat)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>VAT {vatPct}%</span><span>{money(inv.vat_total)}</span></div>
              <div className="flex justify-between font-display font-bold text-base border-t border-border pt-2"><span>{isCN ? "Credit total" : "Total (incl VAT)"}</span><span data-testid="invoice-total">{money(inv.total)}</span></div>
              {!isCN && (inv.amount_paid > 0 || inv.credits_applied > 0) && (
                <>
                  {inv.amount_paid > 0 && <div className="flex justify-between text-muted-foreground"><span>Received</span><span data-testid="invoice-amount-paid">- {money(inv.amount_paid)}</span></div>}
                  {inv.credits_applied > 0 && <div className="flex justify-between text-muted-foreground"><span>Credit notes applied</span><span data-testid="invoice-credits-applied">- {money(inv.credits_applied)}</span></div>}
                  <div className="flex justify-between font-semibold border-t border-border pt-2"><span>Balance due</span><span data-testid="invoice-balance-due">{money(inv.balance_due)}</span></div>
                </>
              )}
            </div>
          </div>

          {!isCN && inv.payments?.length > 0 && (
            <div className="mt-6 text-xs" data-testid="invoice-payments">
              <p className="font-medium mb-1">Receipts</p>
              {inv.payments.map((p) => <p key={p.id} className="text-muted-foreground">{p.date} · {money(p.amount)} · {MODE_LABEL[p.mode] || p.mode}{p.reference ? ` · ref ${p.reference}` : ""}</p>)}
            </div>
          )}
          {!isCN && inv.credit_notes?.length > 0 && (
            <div className="mt-4 text-xs" data-testid="invoice-credit-notes">
              <p className="font-medium mb-1">Credit notes against this invoice</p>
              {inv.credit_notes.map((c) => <p key={c.order_id} className="text-muted-foreground">{isPublic ? c.invoice_no : <Link to={`/invoice/${c.order_id}`} className="text-primary hover:underline">{c.invoice_no}</Link>} · {c.date} · {money(c.total)}</p>)}
            </div>
          )}

          {!isPublic && inv.pricing_versions && (
            <p className="text-[11px] text-muted-foreground mt-6 print:hidden" data-testid="invoice-pricing-version">
              Priced under {inv.pricing_versions.filter((v) => v.effective_from).map((v) => `${v.product} v.${v.effective_from}`).join(", ") || "current prices"}
              {inv.policy_version ? ` · commission policy v.${inv.policy_version.effective_from} (${Math.round(inv.policy_version.policy_pct * 100)}%)` : ""}
            </p>
          )}
          {!isPublic && <div className="mt-4 print:hidden"><Attachments entityType="invoice" entityId={oid} kinds={["delivery_note", "cheque_scan", "other"]} /></div>}

          {inv.deposit_adjustment && (
            <p className="text-xs text-muted-foreground mt-6 rounded-md bg-secondary/50 p-3">
              Deposit adjustment on this invoice: <span className="capitalize font-medium">{inv.deposit_adjustment.status.replace("_", " ")}</span>
              {inv.deposit_adjustment.deducted_amount != null && ` · ${money(inv.deposit_adjustment.deducted_amount)} adjusted against the agent's security deposit`}
            </p>
          )}

          <p className="text-[11px] text-muted-foreground mt-8">
            {isCN ? "This credit note is applied against the referenced tax invoice and reduces its balance due."
              : `Payment due within ${inv.credit_days} days of the invoice date. Agent commission on this invoice is released only after payment is received in full. Unpaid invoices past their due date may be adjusted against the sales agent's security deposit at the next billing cycle.`}
            {co.invoice_footer && <span className="block mt-1">{co.invoice_footer}</span>}
          </p>
        </div>
      </div>
    </div>
  );
}
